import type { EnhanceSuggestion } from '../types/enhanceModifications';
import { parseSpecificationLines } from './specifications';

/**
 * Pure helpers mirroring enhance review selection rules for unit tests.
 */
export function buildInitialEnhanceSelection(
  suggestions: EnhanceSuggestion[],
  highConfidence = 0.8
): Record<string, boolean> {
  const next: Record<string, boolean> = {};
  suggestions.forEach((s) => {
    s.fields.forEach((field: string) => {
      next[`${s.id}:${field}`] = s.confidence >= highConfidence;
    });
  });
  return next;
}

export function collectApprovedUpdates(
  suggestions: EnhanceSuggestion[],
  selected: Record<string, boolean>,
  edits: Record<string, string> = {}
): Array<{
  id: number;
  fields: Record<string, boolean>;
  values: Record<string, unknown>;
  edited_fields: Record<string, boolean>;
}> {
  const byId = new Map<
    number,
    {
      id: number;
      fields: Record<string, boolean>;
      values: Record<string, unknown>;
      edited_fields: Record<string, boolean>;
    }
  >();

  suggestions.forEach((s) => {
    const fields: Record<string, boolean> = {};
    const values: Record<string, unknown> = {};
    const edited_fields: Record<string, boolean> = {};

    s.fields.forEach((field: string) => {
      const key = `${s.id}:${field}`;
      if (!selected[key]) return;
      fields[field] = true;
      const edited = edits[`${s.id}:${field}:edit`];
      if (edited !== undefined) {
        edited_fields[field] = true;
        values[field] =
          field === 'specifications' ? parseSpecificationLines(edited) : edited;
      } else if (field === 'specifications') {
        values[field] = s.suggested.specifications || [];
      } else if (field === 'parent') {
        values.parent_modification_id = s.suggested.parent_modification_id ?? null;
        values.parent = s.suggested.parent_modification_id ?? null;
      } else {
        values[field] = (s.suggested as Record<string, unknown>)[field];
      }
    });

    if (Object.keys(fields).length > 0) {
      byId.set(s.id, { id: s.id, fields, values, edited_fields });
    }
  });

  return Array.from(byId.values());
}

export type EnhanceFieldStats = {
  shown: number;
  accepted: number;
  edited: number;
  rejected: number;
};

export type EnhanceApplyStats = {
  suggestions_shown: number;
  accepted: number;
  edited: number;
  rejected: number;
  fields: Record<string, EnhanceFieldStats>;
};

const FIELD_KEYS = [
  'title',
  'category',
  'vendor',
  'description',
  'specifications',
  'group_label',
  'parent',
] as const;

export function computeEnhanceApplyStats(
  suggestions: EnhanceSuggestion[],
  selected: Record<string, boolean>,
  edits: Record<string, string> = {}
): EnhanceApplyStats {
  const fields: Record<string, EnhanceFieldStats> = {};
  FIELD_KEYS.forEach((f) => {
    fields[f] = { shown: 0, accepted: 0, edited: 0, rejected: 0 };
  });

  let shown = 0;
  let accepted = 0;
  let edited = 0;
  let rejected = 0;

  suggestions.forEach((s) => {
    s.fields.forEach((field: string) => {
      shown += 1;
      if (!fields[field]) {
        fields[field] = { shown: 0, accepted: 0, edited: 0, rejected: 0 };
      }
      fields[field].shown += 1;

      const key = `${s.id}:${field}`;
      if (selected[key]) {
        accepted += 1;
        fields[field].accepted += 1;
        if (edits[`${s.id}:${field}:edit`] !== undefined) {
          edited += 1;
          fields[field].edited += 1;
        }
      } else {
        rejected += 1;
        fields[field].rejected += 1;
      }
    });
  });

  return {
    suggestions_shown: shown,
    accepted,
    edited,
    rejected,
    fields,
  };
}

export function formatEnhanceRange(offset: number, returned: number, total: number): string {
  if (total <= 0 || returned <= 0) {
    return `0 of ${total} modifications`;
  }
  return `${offset + 1}-${offset + returned} of ${total} modifications`;
}

export function enhanceProgressPercent(offset: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((offset / total) * 100));
}
